// Unit of measure for transaction lines.
//
// A line can be entered in any unit of its item's stock-unit category (2 Ton for an item stocked in
// Kg). The form keeps quantity and rate in the chosen unit and sends `enteredUnitId` with the line;
// the server turns it into stock-unit figures. Units are fetched once and shared by every form.
import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '@/api/client';

export interface UnitDef {
  id: string;
  name: string;
  shortName: string;
  category: string;
  isBase: boolean;
  factor: number;
  allowDecimal: boolean;
  uqcCode: string | null;
  isActive: boolean;
}

let cache: UnitDef[] | null = null;
let pending: Promise<UnitDef[]> | null = null;

function loadUnits(): Promise<UnitDef[]> {
  if (cache) return Promise.resolve(cache);
  if (!pending) {
    pending = apiClient
      .get('/api/v1/unit/all')
      .then((res) => {
        cache = (res.data?.data ?? []).map((u: any) => ({
          id: u.id,
          name: u.name,
          shortName: u.shortName ?? u.short_name,
          category: u.category ?? 'OTHER',
          isBase: Boolean(u.isBase),
          factor: Number(u.factor) || 1,
          allowDecimal: u.allowDecimal !== false,
          uqcCode: u.uqcCode ?? null,
          isActive: u.isActive !== false,
        }));
        return cache!;
      })
      .catch(() => {
        pending = null;
        return [];
      });
  }
  return pending;
}

export const round6 = (n: number) => Math.round(n * 1e6) / 1e6;
export const fmtQty = (n: number) => String(round6(Number(n) || 0));

export interface UnitCatalog {
  units: UnitDef[];
  byId: (id?: string | null) => UnitDef | undefined;
  byCode: (code?: string | null) => UnitDef | undefined;
  /** Units a line of an item stocked in `stockUnit` may be entered in (same category, active). */
  optionsFor: (stockUnit?: string | null) => UnitDef[];
  /** How many stock units one `unitId` is (1 Ton → 1000 for an item in Kg). 1 when unknown. */
  toStock: (unitId: string | null | undefined, stockUnit?: string | null) => number;
  /** Message when `qty` has decimals in a unit counted whole (2.5 Pcs), else null. */
  decimalError: (qty: number, unitIdOrCode?: string | null) => string | null;
}

export function useUnits(): UnitCatalog {
  const [units, setUnits] = useState<UnitDef[]>(cache ?? []);
  useEffect(() => {
    let alive = true;
    loadUnits().then((u) => alive && setUnits(u));
    return () => {
      alive = false;
    };
  }, []);

  return useMemo(() => {
    const idMap = new Map(units.map((u) => [u.id, u]));
    const codeMap = new Map(units.map((u) => [u.shortName.toLowerCase(), u]));
    const byId = (id?: string | null) => (id ? idMap.get(id) : undefined);
    const byCode = (code?: string | null) => (code ? codeMap.get(String(code).trim().toLowerCase()) : undefined);

    const optionsFor = (stockUnit?: string | null) => {
      const stock = byCode(stockUnit);
      if (!stock) return [];
      if (stock.category === 'OTHER') return [stock];
      return units.filter((u) => u.category === stock.category && (u.isActive || u.id === stock.id));
    };

    const toStock = (unitId: string | null | undefined, stockUnit?: string | null) => {
      const unit = byId(unitId);
      const stock = byCode(stockUnit);
      if (!unit || !stock || unit.id === stock.id || unit.category !== stock.category) return 1;
      return unit.factor / stock.factor;
    };

    const decimalError = (qty: number, unitIdOrCode?: string | null) => {
      const unit = byId(unitIdOrCode) ?? byCode(unitIdOrCode);
      const n = Number(qty);
      if (!unit || unit.allowDecimal || !Number.isFinite(n) || Math.abs(n - Math.round(n)) < 1e-9) return null;
      return `${unit.shortName} is counted in whole numbers (${n} is not allowed)`;
    };

    return { units, byId, byCode, optionsFor, toStock, decimalError };
  }, [units]);
}

/**
 * Switching a line to another unit keeps the same physical quantity and value:
 * 2000 Kg @ ₹80 → 2 Ton @ ₹80,000. Returns the new quantity and rate.
 */
export function switchLineUnit(
  catalog: UnitCatalog,
  stockUnit: string | null | undefined,
  fromUnitId: string | null | undefined,
  toUnitId: string | null | undefined,
  qty: number,
  rate?: number,
) {
  const fromToStock = catalog.toStock(fromUnitId, stockUnit);
  const toToStock = catalog.toStock(toUnitId, stockUnit);
  const ratio = fromToStock / toToStock;
  return {
    qty: round6((Number(qty) || 0) * ratio),
    rate: rate === undefined ? undefined : round6((Number(rate) || 0) / ratio),
  };
}

/**
 * Display values of a saved line: what was entered when another unit was used, else the
 * stock-unit figures. `stock` carries the stock-unit quantity for a "(2000 Kg)" hint.
 */
export function enteredView(line: {
  qty?: number;
  quantity?: number;
  rate?: number;
  unit?: string;
  unitName?: string;
  enteredQty?: number | null;
  enteredUnitId?: string | null;
  enteredUnitName?: string | null;
  enteredRate?: number | null;
  entered_qty?: number | string | null;
  entered_unit_id?: string | null;
  entered_unit_name?: string | null;
  entered_rate?: number | string | null;
}) {
  const stockQty = Number(line.qty ?? line.quantity ?? 0);
  const stockUnit = line.unit ?? line.unitName ?? '';
  const eQty = line.enteredQty ?? line.entered_qty;
  const eUnit = line.enteredUnitName ?? line.entered_unit_name;
  const eUnitId = line.enteredUnitId ?? line.entered_unit_id ?? null;
  const eRate = line.enteredRate ?? line.entered_rate;
  if (eQty === null || eQty === undefined || !eUnit) {
    return { qty: stockQty, unit: stockUnit, unitId: null as string | null, rate: Number(line.rate ?? 0), converted: false, stockQty, stockUnit };
  }
  return {
    qty: Number(eQty),
    unit: eUnit,
    unitId: eUnitId,
    rate: eRate === null || eRate === undefined ? Number(line.rate ?? 0) : Number(eRate),
    converted: true,
    stockQty,
    stockUnit,
  };
}

/**
 * Qty, rate and unit to carry from a saved document line into a new one (quotation → invoice):
 * the entered figures when the line was entered in another unit, else the stock-unit ones.
 */
export function carryLineUnit(src: {
  qty?: number;
  rate?: number;
  enteredQty?: number | null;
  enteredUnitId?: string | null;
  enteredRate?: number | null;
}) {
  if (src.enteredUnitId && src.enteredQty != null) {
    return {
      qty: Number(src.enteredQty),
      rate: Number(src.enteredRate ?? src.rate ?? 0),
      enteredUnitId: src.enteredUnitId,
    };
  }
  return { qty: Number(src.qty ?? 0), rate: Number(src.rate ?? 0), enteredUnitId: null as string | null };
}

/**
 * A saved line as it should be shown and printed: qty / unit / rate as entered (2 Ton @ ₹80,000)
 * when another unit than the stock unit was used. Amounts are unchanged. The stock-unit figures
 * stay available as stockQty / stockUnit for a "(2000 Kg)" hint.
 */
export function asEntered<T extends Record<string, any>>(line: T, qtyKey: string = 'qty', unitKey: string = 'unit', rateKey: string = 'rate'): T & { stockQty?: number; stockUnit?: string } {
  const eQty = line.enteredQty ?? line.entered_qty;
  const eUnit = line.enteredUnitName ?? line.entered_unit_name;
  if (eQty === null || eQty === undefined || !eUnit) return line;
  const eRate = line.enteredRate ?? line.entered_rate;
  return {
    ...line,
    stockQty: Number(line[qtyKey] ?? 0),
    stockUnit: line[unitKey],
    [qtyKey]: Number(eQty),
    [unitKey]: eUnit,
    ...(eRate === null || eRate === undefined ? {} : { [rateKey]: Number(eRate) }),
  };
}
